import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { api } from '../services/api';

interface SettingsContextType {
  systemName: string;
  systemLogo: string | undefined;
  flowAutomatedEnabled: boolean;
  isLoading: boolean;
  refreshSettings: () => Promise<void>;
}

const SettingsContext = createContext<SettingsContextType | undefined>(undefined);

export const SettingsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [systemName, setSystemName] = useState('Itororo SAC');
  const [systemLogo, setSystemLogo] = useState<string | undefined>();
  const [flowAutomatedEnabled, setFlowAutomatedEnabled] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const loadSettings = useCallback(async () => {
    try {
      // Load public settings first (no authentication required)
      const publicSettings = await api.getPublicSettings();
      setSystemName(publicSettings.systemName || 'Itororo SAC');
      setSystemLogo(publicSettings.systemLogo || undefined);

      // Try to load protected settings (requires authentication)
      // This will only work if user is logged in
      try {
        const protectedSettings = await api.getSettings();
        setFlowAutomatedEnabled(
          protectedSettings.flowAutomatedEnabled === 'true' ||
          protectedSettings.flowAutomatedEnabled === true
        );
      } catch (authError) {
        // User not authenticated yet - flowAutomatedEnabled stays false
        // This is fine, it will be loaded after login
        setFlowAutomatedEnabled(false);
      }
    } catch (error) {
      console.error('Failed to load settings:', error);
      // Fallback to default
      setSystemName('Itororo SAC');
      setSystemLogo(undefined);
      setFlowAutomatedEnabled(false);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSettings();

    // Listen for login events to reload protected settings
    const handleLoginSuccess = () => {
      console.log('[SettingsContext] Login detected, reloading protected settings');
      loadSettings();
    };

    window.addEventListener('login-success', handleLoginSuccess);

    return () => {
      window.removeEventListener('login-success', handleLoginSuccess);
    };
  }, [loadSettings]);

  // Update page title whenever systemName changes
  useEffect(() => {
    document.title = systemName;
  }, [systemName]);

  // Update favicon whenever systemLogo changes
  useEffect(() => {
    const link = document.querySelector("link[rel*='icon']") as HTMLLinkElement || document.createElement('link');
    link.type = 'image/x-icon';
    link.rel = 'shortcut icon';
    link.href = systemLogo ? `${import.meta.env.VITE_API_URL || 'http://localhost:3001'}${systemLogo}` : '/vite.svg';
    document.getElementsByTagName('head')[0].appendChild(link);
  }, [systemLogo]);

  const refreshSettings = async () => {
    setIsLoading(true);
    await loadSettings();
  };

  return (
    <SettingsContext.Provider value={{ systemName, systemLogo, flowAutomatedEnabled, isLoading, refreshSettings }}>
      {children}
    </SettingsContext.Provider>
  );
};

export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (context === undefined) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
};
